/**
 * SRS State Store
 * Manages spaced repetition cards and persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SRSCard, SRSRating } from '../types/question';
import type { AnsweredQuestionRef } from '../types/game';
import {
  createSRSCard,
  calculateNextReview,
  getDueCards,
  getSRSStats,
  exportCards,
  importCards,
} from '../engine/srs';

interface SRSStore {
  // State
  cards: SRSCard[];

  // Actions
  addCard: (questionId: string) => void;
  rateCard: (questionId: string, rating: SRSRating) => void;
  initializeFromAnswers: (answers: AnsweredQuestionRef[]) => void;

  // Queries
  getDueCards: () => SRSCard[];
  getCardForQuestion: (questionId: string) => SRSCard | undefined;
  getStats: () => {
    total: number;
    due: number;
    new: number;
    learning: number;
    mastered: number;
  };

  // Persistence
  exportData: () => string;
  importData: (json: string) => void;

  // Reset
  reset: () => void;
}

export const useSRSStore = create<SRSStore>()(
  persist(
    (set, get) => ({
      cards: [],

      // Add a new card
      addCard: (questionId) => {
        const exists = get().cards.find(c => c.questionId === questionId);
        if (exists) return;

        const newCard = createSRSCard(questionId);
        set({ cards: [...get().cards, newCard] });
      },

      // Rate a card and update its schedule
      rateCard: (questionId, rating) => {
        const card = get().cards.find(c => c.questionId === questionId);
        if (!card) return;

        const updatedCard = calculateNextReview(card, rating);
        set({
          cards: get().cards.map(c =>
            c.questionId === questionId ? updatedCard : c
          ),
        });
      },

      // Initialize cards from answered questions
      initializeFromAnswers: (answers) => {
        const cardMap = new Map<string, SRSCard>();

        // Keep existing cards
        for (const card of get().cards) {
          cardMap.set(card.questionId, card);
        }

        // Add/update from answers
        for (const answer of answers) {
          const existing = cardMap.get(answer.id);

          if (!existing) {
            // Create new card with initial rating based on correctness
            const card = createSRSCard(answer.id);
            const rating: SRSRating = answer.wasCorrect ? 'good' : 'again';
            const updated = calculateNextReview(card, rating);
            cardMap.set(answer.id, updated);
          } else {
            // Update existing if this is a new answer
            const rating: SRSRating = answer.wasCorrect ? 'good' : 'again';
            const updated = calculateNextReview(existing, rating);
            cardMap.set(answer.id, updated);
          }
        }

        set({ cards: Array.from(cardMap.values()) });
      },

      // Get cards due for review
      getDueCards: () => {
        return getDueCards(get().cards);
      },

      // Get card for a specific question
      getCardForQuestion: (questionId) => {
        return get().cards.find(c => c.questionId === questionId);
      },

      // Get statistics
      getStats: () => {
        return getSRSStats(get().cards);
      },

      // Export data
      exportData: () => {
        return exportCards(get().cards);
      },

      // Import data
      importData: (json) => {
        try {
          const cards = importCards(json);
          set({ cards });
        } catch (e) {
          console.error('Failed to import SRS data:', e);
        }
      },

      // Reset store
      reset: () => set({ cards: [] }),
    }),
    {
      name: 'playing-history-srs',
      version: 1,
    }
  )
);
